import { MenuItem, ListItemIcon, Box, Stack } from '@mui/material';
import { IconButton, Typography, colors } from '@sweep-io/sweep-design';
import { CustomWebhookInterface, useWebhooksContext } from '../webhooks/WebhooksContext';
import { ActionsMenu } from '../../common/actions-menu/ActionsMenu';
import { Delete, Edit, Integrations } from '@sweep-io/sweep-design/dist/icons';
import { LogoDevIcon } from '../../common/logo-dev-selector/LogoDevIcon';
import { useIntegrations } from '../../../hooks/useIntegrations';
import useConfirm from '../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import ConfirmDestructiveDialog from '../../common/dialogs/ConfirmDestructiveDialog';
import { useState } from 'react';
import { UsedIntegrationDialog } from './UsedIntegrationDialog';
import { ManageEnvDialog } from '../../pages/environments/connect-env/ManageEnvDialog';
import { useSelector } from 'react-redux';
import { selectDefaultCreationEnvironment } from '../../pages/environments/environmentsReducer';
import { ConfirmDialog } from '../../common/dialogs/ConfirmDialog';
import usePermission from '../../common/permissions/usePermission';
import RestrictedTooltip from '../../common/permissions/RestrictedTooltip';
import { AuditDialog } from '../../common/audit-table/AuditDialog';
import { AuditReportType } from '../../common/audit-table/AuditTable';

interface CustomIntegrationOptionProps {
  integration: CustomWebhookInterface;
}
enum WebhookActionsEnum {
  DELETE = 'DELETE',
  ACTIVITY = 'ACTIVITY',
}

export const CustomIntegrationsOption = ({ integration }: CustomIntegrationOptionProps) => {
  const { deleteCustomIntegration } = useIntegrations();
  const { setWebhooks, setEditWebhook } = useWebhooksContext();
  const { openConfirm, onCancel, onConfirm } = useConfirm();
  const [usedAutomationList, setUsedAutomationList] = useState([]);
  const [isUsedIntegrationOpen, setIsUsedIntegrationOpen] = useState(false);
  const [isManageEnvOpen, setIsManageEnvOpen] = useState(false);
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const selectedEnv = useSelector(selectDefaultCreationEnvironment);
  const [isAllowedBtnEdit, isAllowedBtnDelete] = usePermission([
    'edit:data-integration:custom-integrations',
    'delete:data-integration:custom-integrations',
  ]);

  const menuActions = [
    {
      label: 'Integration activity',
      value: WebhookActionsEnum.ACTIVITY,
      actionIcon: <Integrations />,
      disabled: false,
    },
    {
      label: 'Delete',
      value: WebhookActionsEnum.DELETE,
      actionIcon: <Delete color={colors.blush[600]} />,
      disabled: !isAllowedBtnDelete,
      disabledTooltip: 'To adjust integration settings, please contact your admin.',
      actionSx: { color: colors.blush[600] },
    },
  ];

  const handleDelete = async (isDryRun: boolean) => {
    if (selectedEnv?.isConnected) {
      try {
        await deleteCustomIntegration(integration?.id, isDryRun);
        //if ok show confirm dialog and resend with no dry-run
        if (isDryRun) {
          const isConfirmed = await openConfirm(
            <ConfirmDestructiveDialog
              open={true}
              onConfirm={onConfirm}
              onClose={onCancel}
              title={`Delete ${integration?.name} integration from Sweep?`}
              confirmText="Delete"
              cancelText="Cancel"
            ></ConfirmDestructiveDialog>,
          );
          if (isConfirmed) {
            handleDelete(false);
          }
        }
      } catch (error: any) {
        //show the used automations list
        const usedAutomationList = error?.response?.data?.automationIds;
        if (usedAutomationList?.length > 0) {
          setUsedAutomationList(usedAutomationList);
          setIsUsedIntegrationOpen(true);
        }
      }
    } else {
      const isConfirmed = await openConfirm(
        <ConfirmDialog
          title="Unable to delete integration"
          confirmText="Manage environment"
          children={
            <Typography variant="body">
              {`Your Salesforce environment ${selectedEnv?.name} is disconnected. Reconnect or remove the environment, then delete the integration.`}
            </Typography>
          }
          isOpen={true}
          handleClose={onCancel}
          onConfirm={onConfirm}
          PaperProps={{ sx: { width: '464px' } }}
          confirmButtonType="error"
        />,
      );
      if (isConfirmed) {
        setIsManageEnvOpen(true);
      }
    }
  };

  return (
    <>
      <MenuItem sx={{ padding: '16px 8px !important' }}>
        <Stack width="100%">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              {integration?.logo ? (
                <Box sx={{ width: '36px' }}>
                  <LogoDevIcon brand={integration.logo} size={24} />
                </Box>
              ) : (
                <ListItemIcon>
                  <Box
                    sx={{
                      background: colors.blue[100],
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      textAlign: 'center',
                      lineHeight: '24px',
                    }}
                  >
                    <Integrations variant={'tiny'} color={colors.blue[600]} />
                  </Box>
                </ListItemIcon>
              )}

              <Box
                sx={{
                  flex: '1',
                  textWrap: 'wrap',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <Typography variant="body-medium" color={colors.grey[900]}>
                  {integration.name}
                </Typography>
                <Typography variant="body" color={colors.grey[700]}>
                  Custom integration
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <RestrictedTooltip
                to={['edit:data-integration:custom-integrations']}
                notAllowedTitle={'To adjust integration settings, please contact your admin.'}
              >
                <IconButton
                  onClick={() => {
                    setEditWebhook(integration);
                    setWebhooks(true);
                  }}
                  variant="flat"
                  size="tiny"
                  disabled={!isAllowedBtnEdit}
                >
                  <Edit />
                </IconButton>
              </RestrictedTooltip>
              <ActionsMenu
                iconSize="tiny"
                actions={menuActions}
                onClick={(action) => {
                  if (action?.value === WebhookActionsEnum.DELETE) {
                    handleDelete(true);
                  }
                  if (action?.value === WebhookActionsEnum.ACTIVITY) {
                    setIsActivityOpen(true);
                  }
                }}
                iconVariant="system"
                menuWidth="220px"
              />
            </Box>
          </Box>
        </Stack>
      </MenuItem>
      {isUsedIntegrationOpen && (
        <UsedIntegrationDialog
          usedAutomationList={usedAutomationList}
          integration={integration}
          isOpen={isUsedIntegrationOpen}
          onClose={() => {
            setIsUsedIntegrationOpen(false);
            setUsedAutomationList([]);
          }}
        />
      )}
      {isManageEnvOpen && (
        <ManageEnvDialog onClose={() => setIsManageEnvOpen(false)} allowDeleteLastEnv={false} />
      )}
      {isActivityOpen && (
        <AuditDialog
          onClose={() => setIsActivityOpen(false)}
          auditType={AuditReportType.INTEGRATION_WEBHOOKS}
          integrationId={integration?.id}
        />
      )}
    </>
  );
};
